import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Compass, User2 } from 'lucide-react';
import { AccountMenu } from '../../components/account/AccountMenu';
import { newrotuneLogoFullBlack } from '../../assets/logo';

const tabs = [
  { to: '/app/explore', label: 'Explore', icon: Compass },
  { to: '/app/profile', label: 'Profile', icon: User2 }
];

/** Matches desktop sidebar rail */
const mobileChromeGradient =
  'bg-gradient-to-b from-white via-[#faf9f7] to-brandCream/45';

/** Opaque footer bar — no alpha on bottom stop so content never shows through */
const mobileFooterBg =
  'bg-gradient-to-b from-white via-[#faf9f7] to-brandCream';

const asideClass =
  'hidden shrink-0 bg-gradient-to-b from-white via-[#faf9f7] to-brandCream/45 shadow-[12px_0_48px_-18px_rgba(59,51,133,0.07)] lg:sticky lg:top-0 lg:flex lg:h-full lg:max-h-full lg:w-[15.5rem] lg:flex-col lg:self-start lg:overflow-hidden lg:py-8 xl:w-[17rem]';

const logoLinkClass =
  'inline-block rounded-xl outline-none ring-offset-2 ring-offset-white transition-transform duration-200 hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-brandLilac/50';

const logoImgClass = 'object-contain drop-shadow-[0_2px_12px_rgba(59,51,133,0.06)]';

export const MainTabsShell: React.FC = () => {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background lg:h-full lg:flex-row">
      <aside className={asideClass} aria-label="Main navigation">
        <div className="flex justify-center px-4 pb-8 pt-0">
          <NavLink to="/app/explore" aria-label="Newrotune — go to Explore" className={logoLinkClass}>
            <img
              src={newrotuneLogoFullBlack}
              alt=""
              className={`h-7 w-auto max-w-[9.5rem] object-left ${logoImgClass}`}
            />
          </NavLink>
        </div>
        <nav className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-3">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end
              className={({ isActive }) =>
                [
                  'group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-soft-spring',
                  isActive
                    ? 'bg-white text-brandDeep shadow-[0_10px_36px_-10px_rgba(118,119,228,0.2),0_0_0_1px_rgba(132,165,157,0.2)]'
                    : 'text-textMuted hover:bg-white/85 hover:text-brandDeep hover:shadow-[0_6px_24px_-12px_rgba(59,51,133,0.08)]'
                ].join(' ')
              }
            >
              {({ isActive }) => {
                const Icon = tab.icon;
                return (
                  <>
                    <span
                      className={[
                        'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-200',
                        isActive
                          ? 'bg-gradient-to-br from-brandLilac to-brandTeal text-white shadow-[0_6px_22px_-4px_rgba(118,119,228,0.45)]'
                          : 'border border-borderSoft/35 bg-white/90 text-brandMuted shadow-sm group-hover:border-brandSage/35 group-hover:text-brandTeal'
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden="true"
                    >
                      <Icon className="h-[1.125rem] w-[1.125rem]" strokeWidth={isActive ? 2.35 : 2} />
                    </span>
                    <span className="leading-snug">{tab.label}</span>
                  </>
                );
              }}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto shrink-0 border-t border-borderSoft/50 px-3 pb-3 pt-4">
          <AccountMenu variant="sidebar" />
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header
          className={`${mobileChromeGradient} shadow-[0_10px_40px_-16px_rgba(59,51,133,0.08)] lg:hidden`}
        >
          <div className="mx-auto grid w-full max-w-7xl grid-cols-[minmax(2.75rem,1fr)_auto_minmax(2.75rem,1fr)] items-center px-4 pb-3.5 pt-4 sm:px-6 sm:pb-4 sm:pt-5">
            <span className="min-w-0" aria-hidden />
            <NavLink to="/app/explore" aria-label="Newrotune — go to Explore" className={logoLinkClass}>
              <img
                src={newrotuneLogoFullBlack}
                alt=""
                className={`h-7 w-auto max-w-[min(100%,9.5rem)] sm:h-7 ${logoImgClass}`}
              />
            </NavLink>
            <div className="flex min-w-0 justify-end">
              <AccountMenu variant="header" />
            </div>
          </div>
        </header>
        <main className="no-scrollbar min-h-0 flex-1 overflow-y-auto pb-[max(8rem,calc(7rem+env(safe-area-inset-bottom,0px)))] lg:pb-0">
          <Outlet />
        </main>
        <footer
          className={`pointer-events-none fixed inset-x-0 bottom-0 z-50 ${mobileFooterBg} shadow-[0_-12px_48px_-18px_rgba(59,51,133,0.08)] lg:hidden`}
        >
          <div className="mx-auto w-full max-w-7xl px-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pt-3 sm:px-4 sm:pt-3.5">
            <nav className="pointer-events-auto" aria-label="Main navigation">
              <div className="flex w-full items-stretch justify-between gap-1.5">
                {tabs.map((tab) => (
                  <NavLink
                    key={tab.to}
                    to={tab.to}
                    end
                    aria-label={tab.label}
                    className={({ isActive }) =>
                      [
                        'group flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1.5 py-2 text-[10px] font-medium transition-all duration-200 ease-soft-spring',
                        isActive
                          ? 'bg-white text-brandDeep shadow-[0_8px_28px_-8px_rgba(118,119,228,0.18),0_0_0_1px_rgba(132,165,157,0.18)]'
                          : 'text-textMuted hover:bg-white/85 hover:text-brandDeep hover:shadow-[0_4px_20px_-10px_rgba(59,51,133,0.08)]'
                      ].join(' ')
                    }
                  >
                    {({ isActive }) => {
                      const Icon = tab.icon;
                      return (
                        <>
                          <span
                            className={[
                              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all duration-200',
                              isActive
                                ? 'bg-gradient-to-br from-brandLilac to-brandTeal text-white shadow-[0_6px_22px_-4px_rgba(118,119,228,0.45)]'
                                : 'border border-borderSoft/35 bg-white/90 text-brandMuted shadow-sm group-hover:border-brandSage/35 group-hover:text-brandTeal'
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            aria-hidden="true"
                          >
                            <Icon className="h-[1.125rem] w-[1.125rem]" strokeWidth={isActive ? 2.35 : 2} />
                          </span>
                          <span className="max-w-[4.75rem] text-center leading-tight">{tab.label}</span>
                        </>
                      );
                    }}
                  </NavLink>
                ))}
              </div>
            </nav>
          </div>
        </footer>
      </div>
    </div>
  );
};
